import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  alpha,
  useTheme
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  LocalShipping as ShippingIcon
} from '@mui/icons-material';

const PIOverviewSection = ({ data, company }) => {
  const theme = useTheme();
  const { purchaseOrder, poCreation, lineItems } = data;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending_approval': return 'warning';
      case 'draft': return 'default';
      case 'sent_to_customer': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  return (
    <Card sx={{ 
      mb: 3,
      borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: '#ffffff',
      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ 
            p: 2, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            <AssignmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main', mb: 0.5 }}>
              Customer Order Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Complete summary of order {purchaseOrder?.po_number || poCreation?.po_number || 'N/A'}
            </Typography>
          </Box>
          <Chip 
            label={(() => {
              const status = poCreation?.po_status || purchaseOrder?.status || 'draft';
              // Capitalize first letter
              return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
            })()} 
            color={getStatusColor(poCreation?.po_status || purchaseOrder?.status)}
            sx={{ 
              fontWeight: 500,
              px: 2,
              py: 2.5,
              fontSize: '0.9rem'
            }}
          />
        </Box>

        <Grid container spacing={3}>
          {/* PO Number & Date */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AssignmentIcon sx={{ fontSize: 20, color: 'info.main' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase' }}>
                  PI Number
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={500}>
                {purchaseOrder?.po_number || poCreation?.po_number || 'N/A'}
              </Typography>
            </Box>
          </Grid>

          {/* Order Date */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarIcon sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase' }}>
                  Order Date
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={500}>
                {formatDate(purchaseOrder?.order_date || poCreation?.po_date)}
              </Typography>
            </Box>
          </Grid>

          {/* Delivery Date */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.warning.main, 0.08),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ShippingIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase' }}>
                  Delivery Date
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={500}>
                {formatDate(purchaseOrder?.requested_delivery_date || poCreation?.requested_delivery_date)}
              </Typography>
            </Box>
          </Grid>

          {/* Total Amount */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ 
              p: 2, 
              borderRadius: 2, 
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MoneyIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase' }}>
                  Total Amount
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={500} color="primary.main">
                {(() => {
                  // Use amount_due if available, otherwise total_amount, otherwise calculate from line items
                  const amount = poCreation?.amount_due || poCreation?.total_amount || poCreation?.total_before_credit;
                  if (amount && parseFloat(amount) > 0) {
                    return new Intl.NumberFormat('en-US', { 
                      style: 'currency', 
                      currency: 'USD' 
                    }).format(amount);
                  }
                  // Calculate from line items if available
                  const { lineItems } = data;
                  if (lineItems && lineItems.length > 0) {
                    const calculatedTotal = lineItems.reduce((sum, item) => {
                      return sum + (parseFloat(item.line_total || item.total_price || 0));
                    }, 0);
                    if (calculatedTotal > 0) {
                      return new Intl.NumberFormat('en-US', { 
                        style: 'currency', 
                        currency: 'USD' 
                      }).format(calculatedTotal);
                    }
                  }
                  return '$0.00';
                })()}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Additional Info */}
        {(poCreation?.customer_po_number || poCreation?.payment_terms) && (
          <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>
            <Grid container spacing={2}>
              {poCreation?.customer_po_number && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                    Customer PI Number
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {poCreation.customer_po_number}
                  </Typography>
                </Grid>
              )}
              {poCreation?.payment_terms && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                    Payment Terms
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {poCreation.payment_terms}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Notes Section */}
        {(poCreation?.customer_notes || poCreation?.internal_notes || poCreation?.special_instructions) && (
          <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.3)}` }}>
            {poCreation?.customer_notes && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  Customer Notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', p: 2, bgcolor: alpha(theme.palette.grey[500], 0.05), borderRadius: 2 }}>
                  {poCreation.customer_notes}
                </Typography>
              </Box>
            )}
            {poCreation?.special_instructions && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  Special Instructions
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', p: 2, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 2 }}>
                  {poCreation.special_instructions}
                </Typography>
              </Box>
            )}
            {poCreation?.internal_notes && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  Internal Notes
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
                  {poCreation.internal_notes}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PIOverviewSection;